import { ExternalLink } from 'lucide-react';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import { RetryPanel } from '@/components/feedback/retry-panel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurrentTenant } from '@/context/tenant-registry-context';
import { useCreatePortalSession } from '@/hooks/use-billing-portal';
import { useSubscription } from '@/hooks/use-subscription';
import { useUsage } from '@/hooks/use-usage';
import { isProvenNoSubscription, subscriptionStatusFacts } from '@/lib/subscription';

export function BillingPage() {
    const { tenantId } = useParams<{ tenantId: string }>();
    const {
        data: subscription,
        isLoading: subLoading,
        error: subError,
        refetch: refetchSubscription,
    } = useSubscription(tenantId);
    const {
        data: usage,
        isLoading: usageLoading,
        isError: usageFailed,
        refetch: refetchUsage,
    } = useUsage(tenantId);
    const { entry: currentTenant, isConfirmed } = useCurrentTenant();
    const portalSession = useCreatePortalSession(tenantId ?? '');

    // Same rule as SubscriptionGuard one level up, and now literally the same
    // predicate: only a proven 404 is an absence. Any other failure means we
    // do not know.
    const subscriptionUnknown = subError != null && !isProvenNoSubscription(subError);

    // Owner-only, and only on a CONFIRMED registry entry — a stale role must
    // not act. The server enforces the real role on the endpoint regardless.
    const isOwner = isConfirmed && currentTenant?.role === 'owner';

    const openPortal = () => {
        portalSession.mutate(undefined, {
            // The URL is short-lived and single-use: go there now. Everything
            // done inside the Portal comes back as webhook-synced state.
            onSuccess: ({ url }) => window.location.assign(url),
            onError: (error) =>
                toast.error(
                    error instanceof Error && error.message
                        ? error.message
                        : 'Billing management is not available right now.'
                ),
        });
    };

    if (subLoading || usageLoading) {
        return <BillingSkeleton />;
    }

    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString();
    };

    /**
     * Both usage cards read the same query, so one failure speaks for both.
     * Built once and rendered twice — the same element in two places.
     */
    const usageUnavailable = usageFailed ? (
        <RetryPanel
            title="Usage figures could not be loaded"
            description="These numbers could not be read just now. Nothing has changed about your quotas."
            onRetry={() => void refetchUsage()}
        />
    ) : null;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold">Billing & Usage</h1>
                <p className="text-muted-foreground">Manage your subscription and monitor usage</p>
            </div>

            {/* Subscription Details */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle>Current Plan</CardTitle>
                        {subscription && (
                            <Badge variant={subscriptionStatusFacts(subscription.status).badge}>
                                {subscription.status}
                            </Badge>
                        )}
                    </div>
                    <CardDescription>Your subscription details</CardDescription>
                </CardHeader>
                <CardContent>
                    {subscriptionUnknown ? (
                        <RetryPanel
                            title="Subscription status unavailable"
                            description="Your subscription could not be loaded right now. This is usually temporary — it does not mean the subscription has gone."
                            onRetry={() => void refetchSubscription()}
                        />
                    ) : subscription ? (
                        <div className="space-y-4">
                            <div>
                                <p className="text-2xl font-bold">{subscription.plan.name}</p>
                                {subscription.plan.description && (
                                    <p className="text-sm text-muted-foreground">
                                        {subscription.plan.description}
                                    </p>
                                )}
                            </div>
                            <div className="grid gap-4 text-sm md:grid-cols-2">
                                <div>
                                    <p className="text-muted-foreground">Current Period</p>
                                    <p>
                                        {formatDate(subscription.currentPeriodStart)} -{' '}
                                        {formatDate(subscription.currentPeriodEnd)}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Created</p>
                                    <p>{formatDate(subscription.createdAt)}</p>
                                </div>
                            </div>
                            {subscription.cancelAtPeriodEnd && (
                                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                                    Your subscription will be canceled at the end of the current period (
                                    {formatDate(subscription.currentPeriodEnd)})
                                </div>
                            )}
                            {isOwner && (
                                <div className="space-y-2 border-t pt-4">
                                    <Button
                                        type="button"
                                        onClick={openPortal}
                                        disabled={portalSession.isPending}
                                        className="gap-2"
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        {portalSession.isPending ? 'Opening…' : 'Manage billing'}
                                    </Button>
                                    <p className="text-xs text-muted-foreground">
                                        Plan changes, payment methods, invoices and cancellation are managed
                                        in the secure Stripe billing portal.
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-2 py-8 text-center">
                            <p className="text-muted-foreground">This workspace has no subscription</p>
                            <p className="mx-auto max-w-sm text-sm text-muted-foreground">
                                Workspaces are created together with their subscription, so this one predates
                                the current funnel.
                            </p>
                            <Button asChild variant="outline" className="mt-2">
                                <a href="mailto:contact@therenva.com">Contact support</a>
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Monthly Usage Metrics */}
            <Card>
                <CardHeader>
                    <CardTitle>Monthly Usage</CardTitle>
                    <CardDescription>
                        {usage ? `Resets on ${formatDate(usage.periodEnd)}` : 'Current billing period'}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {usageUnavailable ??
                        (usage ? (
                            <div className="space-y-6">
                                <UsageMetric
                                    label="Cases"
                                    used={usage.limits.monthly.cases_per_month.used}
                                    limit={usage.limits.monthly.cases_per_month.limit}
                                />
                                <UsageMetric
                                    label="Jobs"
                                    used={usage.limits.monthly.jobs_per_month.used}
                                    limit={usage.limits.monthly.jobs_per_month.limit}
                                />
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <p className="text-muted-foreground">No usage data available</p>
                            </div>
                        ))}
                </CardContent>
            </Card>

            {/* Resource Limits */}
            <Card>
                <CardHeader>
                    <CardTitle>Resource Limits</CardTitle>
                    <CardDescription>These limits do not reset monthly</CardDescription>
                </CardHeader>
                <CardContent>
                    {usageUnavailable ??
                        (usage ? (
                            <div className="space-y-6">
                                <UsageMetric
                                    label="Storage (GB)"
                                    used={usage.limits.fixed.storage_gb.used}
                                    limit={usage.limits.fixed.storage_gb.limit}
                                />
                                {usage.limits.fixed.members && (
                                    <UsageMetric
                                        label="Team Members"
                                        used={usage.limits.fixed.members.used}
                                        limit={usage.limits.fixed.members.limit}
                                    />
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <p className="text-muted-foreground">No usage data available</p>
                            </div>
                        ))}
                </CardContent>
            </Card>
        </div>
    );
}

function UsageMetric({ label, used, limit }: { label: string; used: number; limit: number }) {
    const percentage = limit > 0 ? (used / limit) * 100 : 0;
    const isNearLimit = percentage >= 80;
    const isOverLimit = percentage >= 100;

    return (
        <div className="space-y-2">
            <div className="flex justify-between text-sm">
                <span>{label}</span>
                <span
                    className={
                        isOverLimit
                            ? 'text-destructive font-medium'
                            : isNearLimit
                              ? 'text-yellow-600 font-medium'
                              : ''
                    }
                >
                    {used.toLocaleString()} / {limit.toLocaleString()}
                </span>
            </div>
            <Progress
                value={Math.min(percentage, 100)}
                className={isOverLimit ? '[&>div]:bg-destructive' : ''}
            />
        </div>
    );
}

function BillingSkeleton() {
    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <Skeleton className="h-8 w-48" />
                <Skeleton className="mt-2 h-4 w-64" />
            </div>

            {/* Current Plan Card */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <Skeleton className="h-6 w-28" />
                        <Skeleton className="h-5 w-16 rounded-full" />
                    </div>
                    <Skeleton className="h-4 w-40" />
                </CardHeader>
                <CardContent className="space-y-4">
                    <div>
                        <Skeleton className="h-8 w-32" />
                        <Skeleton className="mt-1 h-4 w-48" />
                    </div>
                    <div className="grid gap-4 text-sm md:grid-cols-2">
                        <div className="space-y-1">
                            <Skeleton className="h-4 w-24" />
                            <Skeleton className="h-4 w-40" />
                        </div>
                        <div className="space-y-1">
                            <Skeleton className="h-4 w-16" />
                            <Skeleton className="h-4 w-24" />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Monthly Usage Card */}
            <Card>
                <CardHeader>
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-4 w-40" />
                </CardHeader>
                <CardContent className="space-y-6">
                    {[1, 2].map((i) => (
                        <div key={i} className="space-y-2">
                            <div className="flex justify-between">
                                <Skeleton className="h-4 w-16" />
                                <Skeleton className="h-4 w-24" />
                            </div>
                            <Skeleton className="h-2 w-full" />
                        </div>
                    ))}
                </CardContent>
            </Card>

            {/* Resource Limits Card */}
            <Card>
                <CardHeader>
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-4 w-52" />
                </CardHeader>
                <CardContent className="space-y-6">
                    {[1, 2].map((i) => (
                        <div key={i} className="space-y-2">
                            <div className="flex justify-between">
                                <Skeleton className="h-4 w-24" />
                                <Skeleton className="h-4 w-24" />
                            </div>
                            <Skeleton className="h-2 w-full" />
                        </div>
                    ))}
                </CardContent>
            </Card>
        </div>
    );
}
